/**
 * InlineAdjust Component - Hover Micro-Controls
 *
 * Shows a small adjustment interface when hovering over an assumption value:
 * - Mini slider for quick adjustments
 * - +/- buttons for step changes
 * - Click value to type directly
 */

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useGraph } from '../GraphContext';
import { NodeId, AssumptionNode } from '../../types';

export interface InlineAdjustProps {
  nodeId: NodeId;
  node: AssumptionNode;
  value: number;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Reference to the trigger element for positioning (required) */
  triggerRef: React.RefObject<HTMLElement | null>;
}

interface Position {
  top: number;
  left: number;
}

export function InlineAdjust({
  nodeId,
  node,
  value,
  onClose,
  onMouseEnter,
  onMouseLeave,
  triggerRef,
}: InlineAdjustProps): React.ReactElement {
  const ctx = useGraph();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus input when editing
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Calculate position based on trigger element (for portal mode)
  useLayoutEffect(() => {
    if (!triggerRef?.current) return;

    const trigger = triggerRef.current;
    const rect = trigger.getBoundingClientRect();

    // Position below the trigger with a small gap
    let top = rect.bottom + 8;
    let left = rect.left;

    // Adjust if popup would go off-screen right
    const popupWidth = 200;
    if (left + popupWidth > window.innerWidth - 16) {
      left = window.innerWidth - popupWidth - 16;
    }

    // Adjust if popup would go off-screen bottom
    const popupHeight = 180;
    if (top + popupHeight > window.innerHeight - 16) {
      top = rect.top - popupHeight - 8;
    }

    setPosition({ top, left });
  }, [triggerRef]);

  // Determine step and range
  const range = node.range || { min: 0, max: value * 2, step: undefined };
  const step = range.step || (node.unit === 'percent' ? 0.1 :
    node.unit === 'currency' ? Math.max(1000, Math.round(value * 0.01)) : 1);

  // Handle slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    ctx.setOverride(nodeId, newValue);
  };

  // Handle +/- buttons
  const handleStep = (direction: 1 | -1) => {
    const newValue = Math.max(range.min, Math.min(range.max, value + step * direction));
    ctx.setOverride(nodeId, newValue);
  };

  // Handle direct input
  const handleInputSubmit = () => {
    const parsed = parseFloat(editValue);
    if (!isNaN(parsed)) {
      const clamped = Math.max(range.min, Math.min(range.max, parsed));
      ctx.setOverride(nodeId, clamped);
    }
    setIsEditing(false);
  };

  // Handle input keydown
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleInputSubmit();
    } else if (e.key === 'Escape') {
      setEditValue(String(value));
      setIsEditing(false);
    }
  };

  // Reset to default
  const handleReset = () => {
    ctx.clearOverride(nodeId);
    onClose();
  };

  const isOverridden = ctx.isOverridden(nodeId);

  // The inner content (shared between portal and non-portal modes)
  const content = (
    <div className="bg-white border border-gray-200 shadow-lg p-2">
      {/* Label */}
      <div className="text-xs text-gray-500 mb-2 flex justify-between items-center">
        <span>{node.label}</span>
        {isOverridden && (
          <button
            onClick={handleReset}
            className="text-gray-400 hover:text-gray-600 text-xs underline"
          >
            Reset
          </button>
        )}
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-2">
        {/* Minus button */}
        <button
          onClick={() => handleStep(-1)}
          className="w-6 h-6 flex items-center justify-center border border-gray-200 hover:bg-gray-100 text-gray-600 text-sm font-medium"
          disabled={value <= range.min}
        >
          −
        </button>

        {/* Slider */}
        <input
          type="range"
          min={range.min}
          max={range.max}
          step={step}
          value={value}
          onChange={handleSliderChange}
          className="flex-1 h-1 bg-gray-200 appearance-none cursor-pointer accent-gray-700"
        />

        {/* Plus button */}
        <button
          onClick={() => handleStep(1)}
          className="w-6 h-6 flex items-center justify-center border border-gray-200 hover:bg-gray-100 text-gray-600 text-sm font-medium"
          disabled={value >= range.max}
        >
          +
        </button>
      </div>

      {/* Current value (click to edit) */}
      <div className="mt-2 text-center">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleInputSubmit}
            onKeyDown={handleKeyDown}
            className="w-full text-center text-sm border border-gray-300 px-2 py-1 focus:outline-none focus:border-gray-500"
          />
        ) : (
          <button
            onClick={() => {
              setEditValue(String(value));
              setIsEditing(true);
            }}
            className="text-sm text-gray-700 hover:text-gray-900 border-b border-dotted border-gray-300"
          >
            {ctx.formatValue(value, node.unit)}
          </button>
        )}
      </div>

      {/* Presets (if available) */}
      {node.presets && Object.keys(node.presets).length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-400 mb-1">Presets</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(node.presets).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => {
                  ctx.setOverride(nodeId, preset.value);
                }}
                className={`text-xs px-2 py-0.5 border ${
                  value === preset.value
                    ? 'bg-gray-100 border-gray-300 text-gray-900'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                title={preset.description}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Portal-based popup with fixed positioning
  return createPortal(
    <div
      ref={containerRef}
      className="fixed z-[9999] min-w-48"
      style={{ top: position.top, left: position.left }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {content}
    </div>,
    document.body
  );
}

export default InlineAdjust;
